/**
 * Copyright (c) 2026, CheckPlus Limited
 */
const DefaultFormData = (strings = {}) => {
  const fields = strings.fields || {};
  const paymentTypeOptions = strings.paymentTypeOptions || {};
  const payerTypeOptions = strings.payerTypeOptions || {};
  const paymentMethodOptions = strings.paymentMethodOptions || {};
  const paymentStatusOptions = strings.paymentStatusOptions || {};

  const fieldText = (id, key, fallback) =>
    (fields[id] && fields[id][key]) || fallback;

  const optionLabel = (group, key, fallback) => {
    const groups = {
      paymentTypeOptions,
      payerTypeOptions,
      paymentMethodOptions,
      paymentStatusOptions,
    };

    return (groups[group] && groups[group][key]) || fallback;
  };

  return [
    {
      id: "payerType",
      label: fieldText("payerType", "label", "Payer Type"),
      value: "individual",
      type: "toggle",
      options: [
        {
          label: optionLabel("payerTypeOptions", "individual", "Individual"),
          value: "individual",
        },
        {
          label: optionLabel("payerTypeOptions", "company", "Organization"),
          value: "company",
        },
      ],
      required: true,
      showOnReceipt: false,
    },
    {
      id: "projectId",
      label: fieldText("projectId", "label", "Project"),
      placeholder: fieldText("projectId", "placeholder", "Project"),
      value: null,
      type: "dropdown",
      options: [],
      required: true,
      showOnReceipt: true,
      section: "payment",
    },
    {
      id: "paymentType",
      label: fieldText("paymentType", "label", "Payment Type"),
      placeholder: fieldText("paymentType", "placeholder", "Payment Type"),
      value: 'payment',
      type: "dropdown",
      options: [
        {
          label: optionLabel("paymentTypeOptions", "payment", "Payment"),
          value: "payment",
        },
        {
          label: optionLabel("paymentTypeOptions", "donation", "Donation"),
          value: "donation",
        },
      ],
      required: true,
      showOnReceipt: true,
      section: "payment",
    },
    {
      id: "date",
      label: fieldText("date", "label", "Transaction Date and Time"),
      placeholder: fieldText("date", "placeholder", "YYYY/MM/DD H:MM"),
      value: new Date().toISOString(),
      valueObj: new Date(),
      type: "datetime",
      required: true,
      showOnReceipt: true,
      section: "payment",
    },
    {
      id: "createdAt",
      label: fieldText("createdAt", "label", "Data Entry Date"),
      placeholder: fieldText("createdAt", "placeholder", "YYYY/MM/DD H:MM"),
      value: new Date().toISOString(),
      valueObj: new Date(),
      type: "datetime",
      required: true,
      showOnReceipt: false,
      useNow: true,
      section: "payment",
    },
    {
      id: "paymentMethod",
      label: fieldText("paymentMethod", "label", "Payment Method"),
      placeholder: fieldText("paymentMethod", "placeholder", "Payment Method"),
      value: "alipay_hk", // set default value for dropdown
      type: "dropdown",
      options: [
        {
          label: optionLabel("paymentMethodOptions", "alipay_hk", "AlipayHK (Hong Kong)"),
          value: "alipay_hk",
        },
        {
          label: optionLabel("paymentMethodOptions", "alipay_ch", "Alipay (China)"),
          value: "alipay_ch",
        },
        {
          label: optionLabel("paymentMethodOptions", "wechatpay_hk", "WeChat Pay HK (Hong Kong)"),
          value: "wechatpay_hk",
        },
        {
          label: optionLabel("paymentMethodOptions", "wechatpay_ch", "WeChat Pay (China)"),
          value: "wechatpay_ch",
        },
        {
          label: optionLabel("paymentMethodOptions", "bocpay", "BoC Pay"),
          value: "bocpay",
        },
        {
          label: optionLabel("paymentMethodOptions", "octopus", "Octopus"),
          value: "octopus",
        },
        {
          label: optionLabel("paymentMethodOptions", "bank_transfer", "Bank Transfer"),
          value: "bank_transfer",
        },
        {
          label: optionLabel("paymentMethodOptions", "check_writing", "Cheque"),
          value: "check_writing",
        },
        {
          label: optionLabel("paymentMethodOptions", "cash", "Cash"),
          value: "cash",
        },
        {
          label: optionLabel("paymentMethodOptions", "paypal_manual", "PayPal"),
          value: "paypal_manual",
        },
      ],
      required: true,
      showOnReceipt: true,
      section: "payment",
    },
    {
      id: "paymentAmount",
      label: fieldText("paymentAmount", "label", "Payment Amount"),
      placeholder: fieldText("paymentAmount", "placeholder", "Payment Amount"),
      value: 0,
      type: "decimal",
      required: true,
      showOnReceipt: true,
      section: "payment",
    },
    {
      id: "transactionFee",
      label: fieldText("transactionFee", "label", "Transaction Fee"),
      placeholder: fieldText("transactionFee", "placeholder", "Transaction Fee"),
      value: 0,
      type: "decimal",
      required: true,
      showOnReceipt: false,
      section: "payment",
    },
    {
      id: "paymentNetAmount",
      label: fieldText("paymentNetAmount", "label", "Payment Net Amount"),
      placeholder: fieldText("paymentNetAmount", "placeholder", "Payment Net Amount"),
      value: 0,
      type: "decimal",
      required: true,
      showOnReceipt: false,
      disabled: true,
      section: "payment",
    },
    {
      id: "paymentStatus",
      label: fieldText("paymentStatus", "label", "Payment Status"),
      placeholder: fieldText("paymentStatus", "placeholder", "Payment Status"),
      value: "approved", // set default value for dropdown
      type: "dropdown",
      options: [
        {
          label: optionLabel("paymentStatusOptions", "approved", "Completed"),
          value: "approved",
        },
        {
          label: optionLabel("paymentStatusOptions", "pending", "Pending"),
          value: "pending",
        },
      ],
      required: true,
      showOnReceipt: false,
      section: "payment",
    },
    {
      id: "orgName",
      label: fieldText("orgName", "label", "Organisation Name"),
      placeholder: fieldText("orgName", "placeholder", "Organisation Name"),
      value: "",
      type: "text",
      required: false,
      showOnReceipt: true,
      hiddenWhenPayerType: "individual",
      section: "user",
    },
    {
      id: "firstName",
      label: fieldText("firstName", "label", "First Name"),
      placeholder: fieldText("firstName", "placeholder", "First Name"),
      value: "",
      type: "text",
      required: true,
      showOnReceipt: true,
      section: "user",
    },
    {
      id: "lastName",
      label: fieldText("lastName", "label", "Last Name"),
      placeholder: fieldText("lastName", "placeholder", "Last Name"),
      value: "",
      type: "text",
      required: true,
      showOnReceipt: true,
      section: "user",
    },
    {
      id: "email",
      label: fieldText("email", "label", "Email Address"),
      placeholder: fieldText("email", "placeholder", "Email Address"),
      value: "",
      type: "text",
      required: true,
      showOnReceipt: true,
      section: "user",
    },
    {
      id: "contact",
      label: fieldText("contact", "label", "Contact Number"),
      placeholder: fieldText("contact", "placeholder", "Contact Number"),
      value: "",
      type: "phone",
      required: true,
      showOnReceipt: true,
      section: "user",
    },
    {
      id: "note",
      label: fieldText("note", "label", "Note"),
      placeholder: fieldText("note", "placeholder", "Note"),
      value: "",
      type: "textarea",
      required: false,
      showOnReceipt: true,
      section: "additional",
    },
    {
      id: "paymentReference",
      label: fieldText("paymentReference", "label", "Payment Reference"),
      placeholder: fieldText("paymentReference", "placeholder", "Enter payment reference"),
      value: "",
      type: "text",
      required: false,
      showOnReceipt: false,
      section: "additional",
    },
    {
      id: "proofPayment",
      label: fieldText("proofPayment", "label", "Proof of Payment"),
      placeholder: fieldText("proofPayment", "placeholder", "Upload Proof of Payment"),
      value: "",
      file: null,
      type: "file",
      required: false,
      showOnReceipt: false,
      section: "additional",
    },
  ];
};

export default DefaultFormData;
